import { useState, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import { useTestingApi } from '../hooks/useTestingApi';
import { useEndpoints } from '../../hooks/useEndpoints';
import { formatFullName } from '../../utils/functions';
import { ITestDefinition } from '../../models/testDefinition';
import { ITestResult } from '../../models/testResult';
import { CategoryKey, TeamUser } from './testingTypes';
import TestingTopBar from './TestingTopBar';
import TestingSummaryStats from './TestingSummaryStats';
import TestingEntryTable from './TestingEntryTable';
import TestingAnalyticsView from './TestingAnalyticsView';
import styles from './TestingView.module.scss';

interface TestingViewProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  mode: 'entry' | 'analytics';
  setMode: (mode: 'entry' | 'analytics') => void;
  teamUsers?: TeamUser[];
  teamId: string;
  onOpenTestConfig?: () => void;
}

export default function TestingView({ selectedDate, onDateChange, mode, setMode, teamUsers = [], teamId, onOpenTestConfig }: TestingViewProps) {
  const { catalog, postResult, deleteResult, teamConfig } = useTestingApi(teamId);
  const endpoints = useEndpoints();

  const athletes = useMemo(() => {
    return teamUsers.map((u) => {
      // sez.8 "Per ruolo": ricava posizione dal campo roles[] o position singolo.
      // roles[] è il campo standard di IAthlete; position è il campo unico posizione atleta.
      // Si usa il primo elemento non vuoto come position per il raggruppamento.
      const rolesArray: string[] = Array.isArray((u as any).roles)
        ? (u as any).roles
        : (u as any).position
        ? [(u as any).position]
        : [];
      const position = rolesArray.find((r: string) => r?.trim()) ?? undefined;

      return {
        id: u._id,
        name: formatFullName(u.firstName, u.lastName),
        position,
      };
    });
  }, [teamUsers]);

  const [selectedCategory, setSelectedCategory] = useState<CategoryKey>('forza');

  const enabledTestIds = useMemo(() => {
    if (!teamConfig?.enabledTests) return undefined; // config non ancora caricata
    return (teamConfig.enabledTests as any[]).map((t: any) => typeof t === 'string' ? t : t.testId);
  }, [teamConfig]);

  const hasNoTestsConfigured = Array.isArray(enabledTestIds) && enabledTestIds.length === 0;

  const categoryTests = useMemo(() => {
    if (hasNoTestsConfigured) return [];
    return catalog.filter((test: ITestDefinition) => {
      if (test.category !== selectedCategory) return false;
      if (enabledTestIds && enabledTestIds.length > 0) return enabledTestIds.includes(test.id);
      return true;
    });
  }, [selectedCategory, catalog, enabledTestIds, hasNoTestsConfigured]);

  const teamResultQueries = useQueries({
    queries: categoryTests.map((test: ITestDefinition) => ({
      queryKey: ['testing-results-team', teamId, test.id, selectedDate],
      queryFn: async () => {
        const { data } = await endpoints.getTeamTestResults(teamId!, { testId: test.id, date: selectedDate });
        return { testId: test.id, results: data };
      },
      enabled: !!teamId && !!test.id && !!selectedDate,
      staleTime: 1000 * 30,
    })),
  });

  const athleteResultQueries = useQueries({
    queries: athletes.flatMap((athlete) =>
      categoryTests.map((test: ITestDefinition) => ({
        queryKey: ['testing-results-athlete', athlete.id, test.id],
        queryFn: async () => {
          const { data } = await endpoints.getAthleteTestResults(athlete.id, { testId: test.id, limit: 50 });
          return { athleteId: athlete.id, testId: test.id, results: data };
        },
        enabled: !!athlete.id && !!test.id,
        staleTime: 1000 * 60 * 5,
      }))
    ),
  });

  const dateResultsMap = useMemo(() => {
    const map: Record<string, ITestResult> = {};
    teamResultQueries.forEach((q) => {
      if (q.data) {
        q.data.results.forEach((r: ITestResult) => {
          map[`${r.athleteId}_${r.testId}`] = r;
        });
      }
    });
    return map;
  }, [teamResultQueries]);

  const historyMap = useMemo(() => {
    const map: Record<string, ITestResult[]> = {};
    athleteResultQueries.forEach((q) => {
      if (q.data) {
        const { athleteId, testId, results } = q.data;
        const sorted = [...results].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        map[`${athleteId}_${testId}`] = sorted;
      }
    });
    return map;
  }, [athleteResultQueries]);

  const allApiResults = useMemo(() => {
    const results: { id: string; athleteId: string; testId: string; date: string; value: number }[] = [];
    Object.values(historyMap).forEach((history) => {
      history.forEach((r) => {
        results.push({ id: r._id, athleteId: r.athleteId, testId: r.testId, date: r.date, value: r.value });
      });
    });
    return results;
  }, [historyMap]);

  return (
    <>
      <TestingTopBar
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        mode={mode}
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        allApiResults={allApiResults}
        athletes={athletes}
      />

      {hasNoTestsConfigured ? (
        <div className={styles.emptyState}>
          <Settings2 className={styles.emptyStateIcon} />
          <p className={styles.emptyStateTitle}>Nessun test configurato</p>
          <p className={styles.emptyStateText}>Configura i test abilitati per questo team per iniziare a raccogliere dati.</p>
          {onOpenTestConfig && (
            <button className={styles.emptyStateCta} onClick={onOpenTestConfig}>
              Configura test
            </button>
          )}
        </div>
      ) : mode === 'entry' ? (
        <>
          <TestingSummaryStats
            categoryTests={categoryTests}
            athletes={athletes}
            dateResultsMap={dateResultsMap}
            enabledTests={teamConfig?.enabledTests as any}
          />
          <TestingEntryTable
            categoryTests={categoryTests}
            athletes={athletes}
            dateResultsMap={dateResultsMap}
            historyMap={historyMap}
            selectedDate={selectedDate}
            teamId={teamId}
            catalog={catalog}
            postResult={postResult}
            deleteResult={deleteResult}
            enabledTests={teamConfig?.enabledTests as any}
          />
        </>
      ) : (
        <TestingAnalyticsView
          categoryTests={categoryTests}
          athletes={athletes}
          historyMap={historyMap}
          enabledTests={teamConfig?.enabledTests as any}
        />
      )}
    </>
  );
}
